import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { translations } from '../data/translations';

interface HeaderProps {
  currentPage: string;
  setCurrentPage: (page: string) => void;
}

const Header: React.FC<HeaderProps> = ({ currentPage, setCurrentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (page: string, href?: string) => {
    if (page === 'gallery') {
      setCurrentPage('gallery');
    } else if (page === 'home') {
      setCurrentPage('home');
      // Scroll to top when clicking home
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (href) {
      setCurrentPage('home');
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    setIsMenuOpen(false);
  };

  const navItems = [
    { name: t.home, page: 'home', href: '#home' },
    { name: t.services, page: 'services', href: '#services' },
    { name: t.pricing, page: 'pricing', href: '#pricing' },
    { name: t.about, page: 'about', href: '#about' },
    { name: t.gallery, page: 'gallery' },
    { name: t.booking, page: 'booking', href: '#booking' },
    { name: t.contact, page: 'contact', href: '#contact' },
  ];

  const textColor = currentPage === 'home' 
    ? (isScrolled ? 'text-black' : 'text-white')
    : 'text-black';

  const hoverColor = currentPage === 'home'
    ? (isScrolled ? 'hover:text-gray-600' : 'hover:text-gray-300')
    : 'hover:text-gray-600';

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || currentPage !== 'home'
          ? 'bg-white/95 backdrop-blur-sm shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 sm:py-4">
          {/* Logo */}
          <div className="flex items-center cursor-pointer touch-target" onClick={() => handleNavClick('home')}>
            <img
              src="/logo-optimized.png"
              alt="Hot Dogs Logo"
              className="h-8 sm:h-10 md:h-12 w-auto transition-all duration-300"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavClick(item.page, item.href)}
                className={`${textColor} ${hoverColor} transition-colors duration-300 font-sans font-medium text-sm xl:text-base touch-target px-2 py-2`}
              >
                {item.name}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              className={`p-2 rounded-xl transition-colors duration-300 touch-target ${
                currentPage === 'home' && !isScrolled
                  ? 'bg-white/20 hover:bg-white/30 text-white'
                  : 'bg-light-gray hover:bg-gray-300 text-black'
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-light-gray animate-slide-up bg-white/95 backdrop-blur-sm rounded-b-2xl mx-4 -mx-4">
            <nav className="flex flex-col space-y-2 px-4">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.page, item.href)}
                  className="text-black hover:text-gray-600 transition-colors duration-300 font-sans font-medium py-3 text-left touch-target rounded-xl hover:bg-light-gray px-4"
                >
                  {item.name}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;